import React, { useCallback, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AddIcon from '@mui/icons-material/Add';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutlined';
import LaunchIcon from '@mui/icons-material/Launch';
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Typography,
} from '@mui/material';
import { useSnackbar } from 'notistack';

import { useClaimSubmission } from '../../../store';

interface ResubmitErrorDialogProps {
  open: boolean;
  onClose: () => void;
  error: { message?: string; error_code?: string } | null;
  encounterId?: string | null;
  patientId?: string | null;
  /** The claim ID that failed resubmit (for navigation fallback) */
  claimId?: string | null;
  resubmitType: '7' | '8';
}

function getErrorExplanation(
  error: { message?: string; error_code?: string } | null,
  resubmitType: '7' | '8',
): { title: string; explanation: string; showCreateNew: boolean } {
  const type = resubmitType === '7' ? 'correction' : 'void';
  const message = error?.message ?? '';

  if (message.includes('payer claim control number') || message.includes('frequency code 1')) {
    return {
      title: `Cannot create ${type}`,
      explanation:
        `This claim doesn't have a payer claim control number, which means the payer hasn't assigned a reference to the original submission. ` +
        `Instead of filing a ${type}, you can create a new original claim from the same encounter.`,
      showCreateNew: true,
    };
  }

  if (message) {
    return {
      title: `Cannot create ${type}`,
      explanation: message,
      showCreateNew: !!error?.error_code?.includes('validation'),
    };
  }

  return {
    title: `Failed to create ${type}`,
    explanation: `An unexpected error occurred. Please try again or contact support.`,
    showCreateNew: false,
  };
}

export const ResubmitErrorDialog: React.FC<ResubmitErrorDialogProps> = ({
  open,
  onClose,
  error,
  encounterId,
  patientId,
  claimId,
  resubmitType,
}) => {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const { submitClaim } = useClaimSubmission();
  const [isCreating, setIsCreating] = useState(false);

  const { title, explanation, showCreateNew } = getErrorExplanation(error, resubmitType);
  const canCreateNew = showCreateNew && encounterId && patientId;
  // When we can't create a new claim (no encounter ID), offer to view the claim detail
  const canViewClaim = showCreateNew && !canCreateNew && claimId;

  const handleCreateNewClaim = useCallback(async () => {
    if (!encounterId || !patientId) return;
    setIsCreating(true);
    try {
      const result = await submitClaim({
        encounter_ids: [encounterId],
        patient_id: patientId,
      });
      if (result.success && result.data) {
        enqueueSnackbar('New claim created', { variant: 'success' });
        navigate(`/jotbill/claim/${result.data.id}`);
        onClose();
      } else {
        enqueueSnackbar('Failed to create new claim', { variant: 'error' });
      }
    } catch {
      enqueueSnackbar('Failed to create new claim', { variant: 'error' });
    } finally {
      setIsCreating(false);
    }
  }, [encounterId, patientId, submitClaim, navigate, enqueueSnackbar, onClose]);

  const handleViewClaim = useCallback(() => {
    if (claimId) {
      navigate(`/jotbill/claim/${claimId}`);
      onClose();
    }
  }, [claimId, navigate, onClose]);

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth={"sm"}>
      <DialogTitle sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        <ErrorOutlineIcon color="error" />
        {title}
      </DialogTitle>
      <DialogContent>
        <Typography variant="body2" sx={{ mb: 2 }}>
          {explanation}
        </Typography>
        {canCreateNew && (
          <Typography variant="body2" color="text.secondary">
            A new original claim will be created from the same encounter data and can be submitted directly.
          </Typography>
        )}
        {canViewClaim && (
          <Typography variant="body2" color="text.secondary">
            Open the claim detail to find the linked encounter and create a new claim from there.
          </Typography>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="inherit">
          {canCreateNew || canViewClaim ? 'Cancel' : 'Close'}
        </Button>
        {canCreateNew && (
          <Button
            variant="contained"
            startIcon={isCreating ? <CircularProgress size={16} /> : <AddIcon />}
            onClick={handleCreateNewClaim}
            disabled={isCreating}
          >
            {isCreating ? 'Creating...' : 'Create New Claim'}
          </Button>
        )}
        {canViewClaim && (
          <Button
            variant="contained"
            startIcon={<LaunchIcon />}
            onClick={handleViewClaim}
          >
            View Claim
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};
